import React, { useEffect } from 'react';
import { Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import DateTimePicker from 'react-datetime-picker';
import 'rc-slider/assets/index.css';
import moment from 'moment';
import { useGetActivitySettingsMutation } from '../../../features/authentication/persistApiSlice';
import { useActivityContext } from '../context';
import PropTypes from 'prop-types';


const ActivitySettings = ({ twoColumn }
) => {
  const {
    setAvailabilityOption,
    availabilityOption,
    setActivityMode,
    activityMode,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setTextToSpeech,
    textToSpeech,
    setInitialFromValue,
    initialFromValue,
    setInitialToValue,
    initialToValue,
    studentFileUpload,
    setStudentFileUpload,
    randomized,
    setRandomized
  } = useActivityContext();

  const studentTools = [
    {
      id: 'textToSpeech',
      description: 'Disable Text-to-Speech',
      checked: textToSpeech,
      onChangeEvent: setTextToSpeech
    },
    {
      id: 'randomize',
      description: 'Randomize Vocabulary Order',
      onChangeEvent: setRandomized,
      checked: randomized
    },
    {
      id: 'allowStudentsToUploadFiles',
      description: 'Allow Students to Upload Files',
      checked: studentFileUpload,
      onChangeEvent: setStudentFileUpload,
    }
  ];

  const [getActivitySettings] = useGetActivitySettingsMutation();

  useEffect(() => {
    getActivitySettingsAsync();
  }, []);

  const getActivitySettingsAsync = async()=>{
    const persistData = await getActivitySettings().unwrap();
    setActivityMode(persistData.flashcardMode);
    
    if(persistData.availability == 'FromTo') {
      onChangeInitializeToFrom();
    }
    setAvailabilityOption(persistData.availability);
    setTextToSpeech(persistData.textToSpeech);
    setStudentFileUpload(persistData.studentFileUpload);
    setRandomized(persistData.randomize);
  };

  const onChangeInitialFrom = (value) => {
    if (value === null) {
      setStartDate();
      setInitialFromValue();
    } else {
      const formattedDate = moment(value).format('YYYY/MM/DD h:mm A');
      setStartDate(formattedDate);
      setInitialFromValue(value);
    }
  };

  const onChangeInitializeToFrom = () => {
    var fromDateTime = new Date();
    fromDateTime.setHours(1);
    fromDateTime.setMinutes(0);
    var toDateTime = new Date();
    toDateTime.setHours(23);
    toDateTime.setMinutes(59);
    setInitialFromValue(fromDateTime);
    setInitialToValue(toDateTime);
  };

  const onChangeInitialTo = (value) => {
    if (value === null) {
      setEndDate();
      setInitialToValue();
    } else {
      const formattedDate = moment(value).format('YYYY/MM/DD h:mm A');
      setEndDate(formattedDate);
      setInitialToValue(value);
    }
  };

  useEffect(() => {
    setStartDate(initialFromValue);
    setEndDate(initialToValue);
  }, [initialFromValue, initialToValue]);


  return (
    <Col
      className={`assign-box ${twoColumn ? 'col-lg-6' : 'col-lg-12'} p-20`}>
      <div className="m-3">
        <div className="assign-box-header">
          <div className='align-items-center d-flex gap-2 mb-2'>
            <span className="fs-5 fw-500">Flashcard Settings</span>
          </div>
          <div className='assign-settings'>
            <div className="assign-settings-header my-2">
                Assign to checked students using the following options
            </div>
          </div>
        </div>
        <div className='assign-box-content'>
          <div>
            <div className="d-flex gap-2 my-3 align-items-center">
              <div className="assign-options">Initial Flashcard Options</div>
              <OverlayTrigger
                key={'assignment-options'}
                placement="left"
                overlay={
                  <Tooltip className="initial-assignment-tooltip">
                    <div>
                      <div className='px-3 text-start'>
                        <p>
                          The <strong>Mode</strong> indicates how students can interact
                          with the flashcard.
                        </p>
                        <div>
                          <ul>
                            <li>
                              <strong>Open</strong>: allows students full access to all questions,
                            with all available feedback.
                            </li>                     
                          </ul>
                        </div>
                        <p>
                          The <strong>Availability Options</strong> controls when a student has
                          access to the flashcard
                        </p>
                        <div>
                          <ul>
                            <li>
                              <strong>Always:</strong> allows students full access to all questions at
                              all times.
                            </li>
                            <li>
                              <strong>From/To:</strong> allows students to work on the flashcard
                              within a date and time range.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                }>
                <FontAwesomeIcon className="circle-question" icon={faCircleQuestion} />
              </OverlayTrigger>
            </div>

            <div className="assign-settings-header">
              Availability Options
              <span className='required'>*</span>
            </div>

            <Form.Group className="mt-1" id="availabilityRadioButtonGroup">
              <Form.Check
                inline
                label="Always"
                name="availabilityRadioGroup"
                type="radio"
                id="alwaysAvailabilityRadio"
                checked={availabilityOption == 'Always'}
                onChange={() => setAvailabilityOption('Always')}
              />
              <Form.Check
                inline
                label="From/To"
                name="availabilityRadioGroup"
                type="radio"
                id="frontToAvailabilityRadio"
                checked={availabilityOption == 'FromTo'}
                onChange={() => {setAvailabilityOption('FromTo'); onChangeInitializeToFrom();}}
              />
            </Form.Group>

            {availabilityOption == 'FromTo' && (
              <div className="d-flex gap-5 align-items-center justify-content-between col-10">
                <div className="col-6">
                  <div className="my-3 assign-settings-header">From</div>
                  <div className="date-time-picker">
                    <DateTimePicker
                      onChange={onChangeInitialFrom}
                      value={startDate}
                      format="MM/dd/yyyy h:mm a"
                      className="d-flex"
                      calendarClassName="s-date-calendar scale-6"
                      id="initial-from-date-picker"
                      disableClock={true}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="my-3 assign-settings-header">To</div>
                  <div className="date-time-picker">
                    <DateTimePicker
                      onChange={onChangeInitialTo}
                      value={endDate}
                      format="MM/dd/yyyy h:mm a"
                      className="d-flex"
                      calendarClassName="s-date-calendar scale-6"
                      id="initial-to-date-picker"
                      disableClock={true}
                    />
                  </div>
                </div>
              </div>
            )}
            <div>
              <div className="assign-settings-header mt-3">
                  Modes
                <span className='required'>*</span>
              </div>
              <div>
                <Form.Group className="mb-3 mt-1" id="modeRadioButtonGroup">
                  <Form.Check
                    inline
                    label="Open"
                    name="modeRadioGroup"
                    type="radio"
                    checked={activityMode.toLowerCase() == 'open'}
                    id="openModeRadio"
                    onChange={() => {
                      setActivityMode('open');
                    }}
                  />
                </Form.Group>
              </div>
            </div>
          </div>
          <div className="my-4 d-flex gap-2 align-items-center assign-settings-header">
            <div className="assign-options">Additional Options</div>
          </div>
          {studentTools.map((item, index) => {
            return (
              <Form.Check key={`student-tool${index}`} id={`${item.id}Checkbox`}>
                <Form.Check.Input
                  type="checkbox"
                  checked={item.checked}
                  onChange={(e) => item.onChangeEvent(e.target.checked)}
                />
                <Form.Check.Label className="ms-2">
                  {item.description}
                </Form.Check.Label>
              </Form.Check>
            );
          })}
        </div>
      </div>
    </Col>
  );
};
ActivitySettings.propTypes = {
  twoColumn: PropTypes.bool
};
export default ActivitySettings;
